/**
 * Hero Banner – Hand Scroll Animation
 *
 * Scroll-scrubbed parallax for .hero-banner__hand-image and
 * .hero-banner__hand-overlay. Both start shifted down on the Y axis
 * and rise into their natural position as the user scrolls the
 * section into view. Scrolling back up reverses the motion.
 *
 * Respects prefers-reduced-motion, includes GSAP polling + fallback,
 * and cleans up ScrollTrigger on disconnect.
 */

class HeroBannerHandReveal extends HTMLElement {
  constructor() {
    super();
    this.timeline = null;
    this.fallbackTimer = null;
  }

  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      /* Nothing to do — elements are already in their natural position */
      return;
    }

    /* Skip the animation on mobile / small screens */
    if (window.matchMedia('(max-width: 749px)').matches) {
      return;
    }

    if (document.readyState === 'complete') {
      this.init();
    } else {
      window.addEventListener('load', () => this.init());
    }

    /* Safety fallback — clear transforms after 5 s if GSAP never loads */
    this.fallbackTimer = setTimeout(() => this.showFallback(), 5000);
  }

  /* -------- GSAP helpers -------- */

  getGSAP() {
    if (typeof window !== 'undefined' && window.gsap && typeof window.gsap.to === 'function') {
      return window.gsap;
    }
    if (typeof window !== 'undefined' && window.GSAP && typeof window.GSAP.to === 'function') {
      return window.GSAP;
    }
    if (typeof window !== 'undefined' && window.GreenSockGlobals && window.GreenSockGlobals.gsap) {
      return window.GreenSockGlobals.gsap;
    }
    if (typeof gsap !== 'undefined' && typeof gsap.to === 'function') {
      return gsap;
    }
    return null;
  }

  waitForGSAP() {
    return new Promise((resolve) => {
      if (this.getGSAP()) {
        resolve();
        return;
      }

      var attempts = 0;
      var check = setInterval(() => {
        attempts++;
        if (this.getGSAP() || attempts > 50) {
          clearInterval(check);
          resolve();
        }
      }, 100);
    });
  }

  /* -------- Initialisation -------- */

  async init() {
    await this.waitForGSAP();
    var gsapLib = this.getGSAP();

    if (!gsapLib) {
      this.showFallback();
      return;
    }

    /* Cancel the safety fallback */
    if (this.fallbackTimer) {
      clearTimeout(this.fallbackTimer);
      this.fallbackTimer = null;
    }

    var sectionId = this.getAttribute('data-section-id');
    var section = document.getElementById('HeroBanner-' + sectionId);
    if (!section) return;

    var handImage = section.querySelector('.hero-banner__hand-image');
    var handOverlay = section.querySelector('.hero-banner__hand-overlay');

    /* Need at least one element to animate */
    if (!handImage && !handOverlay) return;

    /* Register ScrollTrigger */
    var hasScrollTrigger = typeof ScrollTrigger !== 'undefined' ||
                           (gsapLib.plugins && gsapLib.plugins.scrollTrigger);

    if (!hasScrollTrigger) {
      /* Without ScrollTrigger a scrub animation is impossible */
      return;
    }

    if (gsapLib.registerPlugin) {
      try {
        gsapLib.registerPlugin(ScrollTrigger || gsapLib.plugins.scrollTrigger);
      } catch (e) {
        // Already registered
      }
    }

    /*
     * Scroll-scrubbed timeline
     *
     * start  = section top hits the bottom of the viewport (just entering view)
     * end    = section top reaches 10% from the top of the viewport (well in view)
     * scrub  = 0.5 for smooth interpolation
     *
     * Both hand elements begin shifted down and rise to their natural position.
     * Scrolling back up reverses the motion.
     */
    this.timeline = gsapLib.timeline({
      scrollTrigger: {
        trigger: section,
        start: 'top bottom',
        end: 'top 10%',
        scrub: 0.5
      }
    });

    /* Animate both targets together — same offset, same timing */
    var targets = [];
    if (handImage) targets.push(handImage);
    if (handOverlay) targets.push(handOverlay);

    this.timeline.fromTo(targets,
      { y: 300 },
      { y: 0, duration: 1, ease: 'none' },
      0
    );
  }

  /* -------- Fallback -------- */

  showFallback() {
    var sectionId = this.getAttribute('data-section-id');
    var section = document.getElementById('HeroBanner-' + sectionId);
    if (!section) return;

    var els = section.querySelectorAll(
      '.hero-banner__hand-image, .hero-banner__hand-overlay'
    );
    els.forEach(function (el) {
      el.style.transform = '';
    });
  }

  /* -------- Cleanup -------- */

  disconnectedCallback() {
    if (this.timeline) {
      if (this.timeline.scrollTrigger) this.timeline.scrollTrigger.kill();
      if (this.timeline.kill) this.timeline.kill();
    }
    if (this.fallbackTimer) {
      clearTimeout(this.fallbackTimer);
    }
  }
}

if (!customElements.get('hero-banner-hand-reveal')) {
  customElements.define('hero-banner-hand-reveal', HeroBannerHandReveal);
}

